import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiClient from '../services/api';
import { toast } from 'sonner';
import MerchantHeader from '../components/MerchantHeader';
import { ApprovalPill, getApprovalState } from '../components/ApprovalPill';
import PolicyGateBadge from '../components/PolicyGateBadge';
import DecisionDrawer from '../components/DecisionDrawer';
import { useSlackStatus } from '../hooks/useSlackStatus';
import { requestSlackApproval } from '../api/slackSettings';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertTriangle,
  FileText,
  Plus,
  Search,
  BookOpen,
  CheckCircle,
  Clock,
  ArrowRight,
  ExternalLink,
  Filter,
  RefreshCw,
  MessageSquare,
  Send
} from 'lucide-react';

const DEFAULT_BRAND_ID = 'demo-brand';

// Decision type icons and labels
const DECISION_TYPES = {
  order_exception: { label: 'Order Exception', color: 'bg-red-500/10 text-red-400' },
  fulfillment_override: { label: 'Fulfillment Override', color: 'bg-orange-500/10 text-orange-400' },
  cancel_approval: { label: 'Cancel Approval', color: 'bg-yellow-500/10 text-yellow-400' },
  substitution_offer: { label: 'Substitution Offer', color: 'bg-blue-500/10 text-blue-400' },
  expedite_request: { label: 'Expedite Request', color: 'bg-purple-500/10 text-purple-400' },
  refund_approval: { label: 'Refund Approval', color: 'bg-green-500/10 text-green-400' }
};

const REASON_CODES = [
  'oversell_confirmed',
  'customer_priority_vip',
  'deadline_imminent',
  'cost_savings',
  'inventory_rebalance',
  'customer_request',
  'quality_issue',
  'shipping_delay',
  'policy_exception'
];

// Loading Skeleton
const LoadingSkeleton = () => (
  <div className="space-y-4">
    {[1, 2, 3].map((i) => (
      <Card key={i} className="bg-card border-border">
        <CardContent className="p-4">
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    ))}
  </div>
);

// Decision Trace Card
const DecisionTraceCard = ({ trace, onViewDetails, onRequestApproval, slackConnected, requestingId }) => {
  const typeInfo = DECISION_TYPES[trace.decision_type] || { 
    label: trace.decision_type, 
    color: 'bg-gray-500/10 text-gray-400' 
  };
  
  const approvalState = getApprovalState(trace);
  const showApprovalButton = approvalState === 'none' || approvalState === 'rejected';
  const isRequesting = requestingId === trace.trace_id;
  
  return (
    <Card 
      className="bg-card border-border hover:border-primary/30 transition-all"
      data-testid={`trace-card-${trace.trace_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div 
            className="flex items-center gap-2 cursor-pointer flex-1"
            onClick={() => onViewDetails(trace)}
          >
            <FileText className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="font-medium text-sm">{trace.object_ref?.type}: {trace.object_ref?.id?.slice(0, 12)}...</p>
              <p className="text-xs text-muted-foreground">
                {new Date(trace.created_at).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ApprovalPill trace={trace} />
            <PolicyGateBadge trace={trace} />
            <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
          </div>
        </div>
        
        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
          {trace.action_taken || 'No action description'}
        </p>
        
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-1">
            {trace.reason_codes?.slice(0, 3).map((code, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {code}
              </Badge>
            ))}
            {trace.reason_codes?.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{trace.reason_codes.length - 3} more
              </Badge>
            )}
          </div>
          
          {showApprovalButton && (
            <Button
              size="sm"
              variant="outline"
              disabled={!slackConnected || isRequesting}
              onClick={(e) => {
                e.stopPropagation();
                onRequestApproval(trace);
              }}
              data-testid={`request-approval-${trace.trace_id}`}
            >
              {isRequesting ? (
                <>
                  <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <MessageSquare className="h-3 w-3 mr-1" />
                  Request Approval
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Precedent Card
const PrecedentCard = ({ precedent, onApply }) => {
  const typeInfo = DECISION_TYPES[precedent.decision_type] || { 
    label: precedent.decision_type, 
    color: 'bg-gray-500/10 text-gray-400' 
  };
  
  return (
    <Card 
      className="bg-card border-border"
      data-testid={`precedent-card-${precedent.precedent_id || precedent._id}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-sm">{precedent.title}</CardTitle>
            <CardDescription className="text-xs mt-1">
              Confidence: {(precedent.confidence_score * 100).toFixed(0)}%
            </CardDescription>
          </div>
          <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-sm text-muted-foreground mb-3">
          {precedent.summary}
        </p>
        
        <div className="flex flex-wrap gap-1 mb-3">
          {precedent.reason_codes?.map((code, idx) => (
            <Badge key={idx} variant="outline" className="text-xs">
              {code}
            </Badge>
          ))}
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-xs text-muted-foreground">
            Citations: {precedent.citation_count || 0}
          </span>
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => onApply(precedent)}
          >
            Apply <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Create Decision Trace Dialog
const CreateTraceDialog = ({ open, onOpenChange, onCreated, brandId }) => {
  const [formData, setFormData] = useState({
    decision_type: '',
    object_ref_type: 'order',
    object_ref_id: '',
    action_taken: '',
    reason_codes: [],
    rationale_template: ''
  });
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async () => {
    if (!formData.decision_type || !formData.object_ref_id || !formData.action_taken) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      await apiClient.post('/api/v1/decision-graph/decision-traces', {
        brand_id: brandId,
        decision_type: formData.decision_type,
        object_ref: {
          type: formData.object_ref_type,
          id: formData.object_ref_id
        },
        action_taken: formData.action_taken,
        reason_codes: formData.reason_codes,
        rationale_template: formData.rationale_template || null
      });
      
      toast.success('Decision trace created');
      onOpenChange(false);
      onCreated();
      setFormData({
        decision_type: '',
        object_ref_type: 'order',
        object_ref_id: '',
        action_taken: '',
        reason_codes: [],
        rationale_template: ''
      });
    } catch (err) {
      toast.error('Failed to create decision trace');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Capture Decision</DialogTitle>
          <DialogDescription>
            Record an exception or decision for future reference
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <label className="text-sm font-medium">Decision Type *</label>
            <Select 
              value={formData.decision_type}
              onValueChange={(v) => setFormData(f => ({ ...f, decision_type: v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(DECISION_TYPES).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div className="grid gap-2">
              <label className="text-sm font-medium">Object Type</label>
              <Select 
                value={formData.object_ref_type}
                onValueChange={(v) => setFormData(f => ({ ...f, object_ref_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="order">Order</SelectItem>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="product">Product</SelectItem>
                  <SelectItem value="fulfillment">Fulfillment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium">Object ID *</label>
              <Input 
                placeholder="e.g., ORD-12345"
                value={formData.object_ref_id}
                onChange={(e) => setFormData(f => ({ ...f, object_ref_id: e.target.value }))}
              />
            </div>
          </div>
          
          <div className="grid gap-2">
            <label className="text-sm font-medium">Action Taken *</label>
            <Textarea 
              placeholder="Describe the decision and action..."
              value={formData.action_taken}
              onChange={(e) => setFormData(f => ({ ...f, action_taken: e.target.value }))}
              rows={3}
            />
          </div>
          
          <div className="grid gap-2">
            <label className="text-sm font-medium">Reason Codes</label>
            <div className="flex flex-wrap gap-1">
              {REASON_CODES.map((code) => (
                <Badge 
                  key={code}
                  variant={formData.reason_codes.includes(code) ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => {
                    setFormData(f => ({
                      ...f,
                      reason_codes: f.reason_codes.includes(code)
                        ? f.reason_codes.filter(c => c !== code)
                        : [...f.reason_codes, code]
                    }));
                  }}
                >
                  {code.replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Creating...' : 'Create Trace'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function ExceptionRadarPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [traces, setTraces] = useState([]);
  const [precedents, setPrecedents] = useState([]);
  const [selectedTrace, setSelectedTrace] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [requestingId, setRequestingId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTrace, setDrawerTrace] = useState(null);
  const brandId = DEFAULT_BRAND_ID;
  
  // Get Slack status for approval button
  const { status: slackStatus } = useSlackStatus(brandId);
  const slackConnected = slackStatus?.connected && slackStatus?.default_channel?.id;
  
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tracesRes, precedentsRes] = await Promise.all([
        apiClient.get(`/api/v1/decision-graph/decision-traces?brand_id=${brandId}&limit=50`),
        apiClient.get(`/api/v1/decision-graph/precedents?brand_id=${brandId}&limit=20`)
      ]);
      
      setTraces(tracesRes.data.traces || []);
      setPrecedents(precedentsRes.data.precedents || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load exception data');
    } finally {
      setLoading(false);
    }
  }, [brandId]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Handle deep link from Slack (?trace=dt_xxx opens drawer)
  useEffect(() => {
    const traceId = searchParams.get('trace');
    if (traceId && traces.length > 0) {
      const trace = traces.find(t => t.trace_id === traceId);
      if (trace) {
        setDrawerTrace(trace);
        setDrawerOpen(true);
      }
    }
  }, [searchParams, traces]);
  
  // Open drawer for a trace
  const openDrawer = (trace) => {
    setDrawerTrace(trace);
    setDrawerOpen(true);
  };
  
  const filteredTraces = traces.filter(trace => {
    if (filterType !== 'all' && trace.decision_type !== filterType) return false;
    if (searchQuery && !trace.action_taken?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });
  
  const handleApplyPrecedent = (precedent) => {
    toast.info(`Applying precedent: ${precedent.title}`);
    // TODO: Implement precedent application flow
  };
  
  // Build summary for Slack approval
  const buildSummary = (trace) => {
    const reasons = (trace.reason_codes || []).join(', ') || '—';
    const note = trace.rationale_text || '—';
    const obj = `${trace.object_ref?.type || 'order'}:${trace.object_ref?.id || '—'}`;
    return `*Decision:* ${trace.action_taken || 'No action'}\n*Object:* ${obj}\n*Reasons:* ${reasons}\n*Note:* ${note}`;
  };
  
  const handleRequestApproval = async (trace) => {
    if (!slackConnected) {
      toast.error('Slack not connected or no default channel set. Configure in Settings.');
      return;
    }
    
    setRequestingId(trace.trace_id);
    try {
      await requestSlackApproval({
        teamId: slackStatus.workspace.team_id,
        brandId,
        traceId: trace.trace_id,
        channelId: undefined, // Use default
        summary: buildSummary(trace)
      });
      toast.success('Approval request sent to Slack');
      // Refresh to update status
      fetchData();
    } catch (e) {
      toast.error(e.message || 'Failed to send approval request');
    } finally {
      setRequestingId(null);
    }
  };
  
  return (
    <div className="min-h-screen bg-background" data-testid="exception-radar-page">
      <MerchantHeader />
      
      <main className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 text-primary" />
              Exception Radar
            </h1>
            <p className="text-muted-foreground mt-1">
              Capture decisions, build precedents, improve over time
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="icon"
              onClick={fetchData}
              data-testid="refresh-button"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-trace-button">
              <Plus className="h-4 w-4 mr-2" />
              Capture Decision
            </Button>
          </div>
        </div>
        
        <Tabs defaultValue="traces" className="space-y-6">
          <TabsList className="bg-muted">
            <TabsTrigger value="traces" data-testid="tab-traces">
              <FileText className="h-4 w-4 mr-2" />
              Decision Traces
            </TabsTrigger>
            <TabsTrigger value="precedents" data-testid="tab-precedents">
              <BookOpen className="h-4 w-4 mr-2" />
              Precedent Library
            </TabsTrigger>
          </TabsList>
          
          {/* Decision Traces Tab */}
          <TabsContent value="traces" className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search decisions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {Object.entries(DECISION_TYPES).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Traces List */}
            {loading ? (
              <LoadingSkeleton />
            ) : filteredTraces.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="traces-grid">
                {filteredTraces.map((trace) => (
                  <DecisionTraceCard 
                    key={trace.trace_id} 
                    trace={trace}
                    onViewDetails={openDrawer}
                    onRequestApproval={handleRequestApproval}
                    slackConnected={slackConnected}
                    requestingId={requestingId}
                  />
                ))}
              </div>
            ) : (
              <Card className="bg-card border-border border-dashed">
                <CardContent className="p-8 text-center">
                  <FileText className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Decision Traces</h3>
                  <p className="text-muted-foreground mb-4">
                    Start capturing decisions to build your exception library
                  </p>
                  <Button onClick={() => setCreateDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Capture First Decision
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* Precedent Library Tab */}
          <TabsContent value="precedents" className="space-y-4">
            {loading ? (
              <LoadingSkeleton />
            ) : precedents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="precedents-grid">
                {precedents.map((precedent) => (
                  <PrecedentCard 
                    key={precedent.precedent_id || precedent._id} 
                    precedent={precedent}
                    onApply={handleApplyPrecedent}
                  />
                ))}
              </div>
            ) : (
              <Card className="bg-card border-border border-dashed">
                <CardContent className="p-8 text-center">
                  <BookOpen className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Precedents Yet</h3>
                  <p className="text-muted-foreground">
                    Precedents are created from frequently used decision patterns
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
        
        {/* Create Dialog */}
        <CreateTraceDialog 
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onCreated={fetchData}
          brandId={brandId}
        />
        
        {/* Decision Drawer - Full context view */}
        <DecisionDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          trace={drawerTrace}
          brandId={brandId}
        />
      </main>
    </div>
  );
}
